'use client'
import { useCallback, useState, useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { type FieldError, type SubmitHandler, useForm } from 'react-hook-form'
import { Select } from '@/components/forms/ui/select'
import { CheckboxGroup } from '@/components/forms/ui/checkbox-group'
import { AI_TOOL_OPTIONS, toAiToolsPayload } from '@/constants/ai-tools'
import { FieldBlock } from '@/components/forms/ui/field-block'
import { Input } from '@/components/forms/ui/input'
import {
  array,
  email,
  literal,
  minLength,
  object,
  optional,
  type InferOutput,
  regex,
  string,
  pipe
} from 'valibot'
import { goToMailError, useSendApi } from '@/components/forms/hook'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { Textarea } from '@/components/forms/ui/textarea'
import { Checkbox } from '@/components/parts/checkbox'
import { ValidationError } from '@/components/forms/ui/validation-error'
import { ActionButton } from '@/components/parts/action-button'
import type { Department } from '@/types/microcms/department'
import { useThanksForAppRouter } from '@/hooks/useThanksForAppRouter'
import type { Opportunity } from '@/types/microcms/opportunity'
import type { GtmDownloadCvFrom } from '@/constants/gtm-cv'

const lawgueRequestSchema = object({
  salesforceId: string(),
  company: pipe(string(), minLength(1, '貴社名/貴所名をご入力ください')),
  lastname: pipe(string(), minLength(1, '必須項目です')),
  firstname: pipe(string(), minLength(1, '必須項目です')),
  email: pipe(
    string(),
    minLength(1, '必須項目です'),
    email('入力されたメールアドレスに間違いがあります')
  ),
  phone_number: pipe(
    string(),
    minLength(1, '必須項目です'),
    regex(/^\d{2,4}-\d{1,4}-\d{4}$/, '電話番号の形式を確認してください')
  ),
  select_form1: pipe(string(), minLength(1, '必須項目です')),
  department: optional(string()),
  select_form2: pipe(string(), minLength(1, '必須項目です')),
  select_form3: pipe(string(), minLength(1, '必須項目です')),
  // 未選択時は RHF から false が渡り配列にならないため、型エラー側にも日本語メッセージを持たせる
  ai_tools: pipe(
    array(string(), '1つ以上選択してください'),
    minLength(1, '1つ以上選択してください')
  ),
  textarea: optional(string()),
  checkbox: literal(true, 'チェックを入れてください'),
  first_view_route: optional(string()),
  last_view_route: optional(string())
})

export type LawgueRequestSchema = InferOutput<typeof lawgueRequestSchema>

const EMPLOYEE_OPTIONS = [
  '1～10人',
  '11～50人',
  '51～100人',
  '101～300人',
  '301～500人',
  '501～1,000人',
  '1,001～3,000人',
  '3,001～5,000人',
  '5,001～10,000人',
  '10,001人以上'
]

type ContactFormProps = {
  service: {
    salesforceId?: string
  }
  departments: Department[]
  opportunities: Opportunity[]
}

// GTM の資料請求 CV は from の値ごとにトリガーがあり、v2 の送信元ページ名で登録されている。
// v3 で新設した製品の資料ページは、役割を引き継いだ v2 のページ名で着地させて既存の CV を発火させる。
// 値は GTM に登録済みのものしか書けない（型で縛っている。一覧は src/constants/gtm-cv.ts）
const THANKS_FROM_BY_SERVICE_ID: Record<string, GtmDownloadCvFrom> = {
  top: 'request', // v2 の /request（総合の資料請求）
  contract: 'contract', // v2 の /contract
  regulation: 'regulation' // v2 の /regulation
}

/** 対応が無いページ（お役立ち資料の数字 ID など）は v2 と同じく送信元のパス（service-<id>）で着地させる */
const getThanksFrom = (pathname: string | null) => {
  const id = pathname?.match(/^\/service\/([^/]+)/)?.[1]
  return id ? THANKS_FROM_BY_SERVICE_ID[id] : undefined
}

/**
 * お役立ち資料のダウンロードフォーム。Figma: rail（type=form）。
 * 詳細ページの右カラム（幅 420）に置く前提で、カード（bg-surface-subtle）ごと描く。
 */
export default function ContactForm({
  service,
  departments,
  opportunities
}: ContactFormProps) {
  const thanksFrom = getThanksFrom(usePathname())
  const { sendApi } = useSendApi(
    '/lawgue-request',
    '/thanks_download/',
    undefined,
    thanksFrom
  )
  const [isButtonClicked, setIsButtonClicked] = useState(false)
  const [firstViewRoute, setFirstViewRoute] = useState('')
  const [lastViewRoute, setLastViewRoute] = useState('')
  const { pushThanks } = useThanksForAppRouter()

  useEffect(() => {
    const fullPath = `${window.location.pathname}${window.location.search}`

    // firstViewURLが未設定の場合のフォールバック処理
    let firstViewURL = sessionStorage.getItem('firstViewURL')
    if (!firstViewURL) {
      firstViewURL = fullPath
      sessionStorage.setItem('firstViewURL', firstViewURL)
    }

    setFirstViewRoute(firstViewURL)
    setLastViewRoute(fullPath)
  }, [])

  const handleOnSubmit: SubmitHandler<LawgueRequestSchema> = useCallback(
    async (data) => {
      const params = new URLSearchParams(window.location.search)
      const formData = {
        ...toAiToolsPayload(data),
        first_view_route: firstViewRoute,
        last_view_route: lastViewRoute,
        utm_params: Object.fromEntries(params)
      }
      setIsButtonClicked(true)
      try {
        const isSent = await sendApi(JSON.stringify(formData))
        // 失敗時は sendApi が送信エラーページへ遷移させるので、サンクスへは進まない
        if (isSent) pushThanks('thanks_download', thanksFrom)
      } catch (error) {
        console.error('Sending API request failed:', error)
        goToMailError()
      } finally {
        setIsButtonClicked(false)
      }
    },
    [sendApi, firstViewRoute, lastViewRoute, pushThanks, thanksFrom]
  )

  const {
    register,
    handleSubmit,
    formState: { errors: formatError, isLoading }
  } = useForm<InferOutput<typeof lawgueRequestSchema>>({
    resolver: valibotResolver(lawgueRequestSchema)
  })

  return (
    <div className="flex flex-col gap-8 rounded-xl bg-surface-subtle p-6 lg:p-8">
      <h2 className="fontfeaturesettings text-center text-2xl font-bold tracking-heading text-ink">
        ダウンロードはこちら
      </h2>
      <form
        className="flex flex-col gap-8"
        onSubmit={handleSubmit(handleOnSubmit)}
      >
        <fieldset className="flex flex-col gap-6">
          <input type="hidden" name="first_view_route" value={firstViewRoute} />
          <input type="hidden" name="last_view_route" defaultValue="/contact" />
          <input
            type="hidden"
            value={
              service.salesforceId?.replace('https://info.lawgue.com', '') || ''
            }
            {...register('salesforceId')}
          />
          <FieldBlock
            labelText={'貴社名／貴所名'}
            htmlFor={'company'}
            required
            error={formatError.company}
          >
            <Input
              id={'company'}
              type="text"
              placeholder="ローグ株式会社"
              autoComplete="company"
              register={register('company')}
            />
          </FieldBlock>
          <div className="flex gap-4">
            <div className="min-w-0 flex-1">
              <FieldBlock
                labelText={'姓'}
                htmlFor={'lastname'}
                required
                error={formatError.lastname}
              >
                <Input
                  id={'lastname'}
                  type="text"
                  placeholder="山田"
                  autoComplete="family-name"
                  register={register('lastname')}
                />
              </FieldBlock>
            </div>
            <div className="min-w-0 flex-1">
              <FieldBlock
                labelText={'名'}
                htmlFor={'firstname'}
                required
                error={formatError.firstname}
              >
                <Input
                  id={'firstname'}
                  type="text"
                  placeholder="太郎"
                  autoComplete="given-name"
                  register={register('firstname')}
                />
              </FieldBlock>
            </div>
          </div>
          <FieldBlock
            labelText={'メールアドレス'}
            htmlFor={'email'}
            required
            error={formatError.email}
          >
            <Input
              id={'email'}
              type="email"
              placeholder="lawgue@mail.com"
              autoComplete="email"
              register={register('email')}
            />
          </FieldBlock>
          <FieldBlock
            labelText={'電話番号（ハイフンあり）'}
            htmlFor={'phone_number'}
            required
            error={formatError.phone_number}
          >
            <Input
              id={'phone_number'}
              type="text"
              placeholder="080-0000-0000"
              autoComplete="phone_number"
              register={register('phone_number')}
            />
          </FieldBlock>
          <FieldBlock
            labelText={'部署区分'}
            htmlFor={'select_form1'}
            required
            error={formatError.select_form1}
          >
            <Select
              id={'select_form1'}
              register={register('select_form1')}
              options={[
                { label: '選択してください。', value: '' },
                ...departments.map((dept) => ({
                  label: dept.title,
                  value: dept.title
                }))
              ]}
            />
          </FieldBlock>
          <FieldBlock labelText={'部署名'} htmlFor={'department'}>
            <Input
              id={'department'}
              type="text"
              placeholder="法務部"
              autoComplete="department"
              register={register('department')}
            />
          </FieldBlock>
          <FieldBlock
            labelText={'従業員数'}
            htmlFor={'select_form2'}
            required
            error={formatError.select_form2}
          >
            <Select
              id={'select_form2'}
              register={register('select_form2')}
              options={[
                { label: '選択してください。', value: '' },
                ...EMPLOYEE_OPTIONS.map((value) => ({ label: value, value }))
              ]}
            />
          </FieldBlock>
          <FieldBlock
            labelText={'LAWGUEを知ったきっかけ'}
            htmlFor={'select_form3'}
            required
            error={formatError.select_form3}
          >
            <Select
              id={'select_form3'}
              register={register('select_form3')}
              options={opportunities.map((opt) => ({
                label: opt.label,
                value: opt.value
              }))}
            />
          </FieldBlock>
          <FieldBlock
            labelText={'業務で使用している生成AIツール（複数可）'}
            htmlFor={'ai_tools_0'}
            required
            error={formatError.ai_tools as FieldError}
          >
            <CheckboxGroup
              id={'ai_tools'}
              register={register('ai_tools')}
              options={AI_TOOL_OPTIONS}
            />
          </FieldBlock>
          <FieldBlock
            labelText={'具体的なお悩みがあればご記入ください'}
            htmlFor={'textarea'}
          >
            <Textarea
              id={'textarea'}
              placeholder="例）文書作成業務の効率化に困っている"
              autoComplete="textarea"
              register={register('textarea')}
            />
          </FieldBlock>
          <div className={formatError.checkbox ? 'error_input' : undefined}>
            <Checkbox htmlfor="checkbox" required register={register} />
            <ValidationError error={formatError.checkbox} />
          </div>
        </fieldset>
        <p className="text-sm leading-compact text-ink">
          同業他社および当社の競合企業にあたる企業の方の資料請求はご遠慮させていただきます。何卒ご了承ください。
        </p>
        <ActionButton
          type="submit"
          label={isButtonClicked ? '送信中' : '送信する'}
          disabled={isButtonClicked || isLoading}
          block
        />
      </form>
    </div>
  )
}
